import { useTranslation } from 'react-i18next';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmButtonStyle?: 'danger' | 'primary' | 'secondary';
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmText,
  cancelText,
  confirmButtonStyle = 'danger',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  
  if (!isOpen) return null;

  const getConfirmButtonClasses = () => {
    switch (confirmButtonStyle) {
      case 'danger':
        return 'bg-[var(--text-default)] hover:bg-[var(--text-muted)] text-[var(--surface)]';
      case 'primary':
        return 'bg-[var(--text-default)] hover:bg-[var(--text-muted)] text-[var(--surface)]';
      case 'secondary':
        return 'bg-[var(--text-default)] hover:bg-[var(--text-muted)] text-[var(--surface)]';
      default:
        return 'bg-[var(--text-default)] hover:bg-[var(--text-muted)] text-[var(--surface)]';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10001] p-4">
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-2xl max-w-md w-full mx-4 transform transition-all">
        {/* Header */}
        <div className="p-6 pb-4 border-b border-[var(--border)]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[var(--text-default)] flex items-center justify-center flex-shrink-0">
              <span className="material-icons text-[var(--surface)] text-xl">warning</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[var(--text-default)]">
                {title}
              </h3>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 pt-4">
          <p className="text-[var(--text-muted)] leading-relaxed">
            {message}
          </p>
        </div>

        {/* Footer */}
        <div className="p-6 pt-4 border-t border-[var(--border)] flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text-default)] hover:bg-[var(--border)] transition-colors duration-200 font-medium"
          >
            {cancelText || t('confirmDialog.defaultCancel')}
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 rounded-lg transition-colors duration-200 font-medium ${getConfirmButtonClasses()}`}
          >
            {confirmText || t('confirmDialog.defaultConfirm')}
          </button>
        </div>
      </div>
    </div>
  );
} 